import { useEffect, useRef } from "react";
import type { Device } from "../api";

// Ein geteilter EventSource auf /events/fleet für die ganze Oberfläche: die WebUI
// hält EINE Live-Verbindung, statt zu pollen. Modul-Store wie useAuth — kein
// Provider, refgezählt auf/zu, damit Fleet-Liste, Freigaben-Badge,
// Freigaben-Tabelle und Detail-Kopf sich denselben Stream teilen.
//
// Nur Server→Browser: der Server pusht Registry-Änderungen (Router online/offline,
// hostname/firmware/wgIp, offene Freigaben). Reconnect macht der EventSource
// selbst. Kein WebSocket — die eine Richtung reicht, und /events/* ist schon
// per Session-Cookie geschützt (docs/device-management.md).

type DeviceHandler = (device: Device) => void;
type EnrollmentHandler = (pending: number) => void;

const deviceListeners = new Set<DeviceHandler>();
const enrollmentListeners = new Set<EnrollmentHandler>();

let source: EventSource | null = null;
let refs = 0;

function open(): void {
  if (refs++ > 0) return;
  source = new EventSource("/events/fleet", { withCredentials: true });
  source.addEventListener("device", (e) => {
    const device = JSON.parse((e as MessageEvent<string>).data) as Device;
    deviceListeners.forEach((fn) => fn(device));
  });
  source.addEventListener("enrollment", (e) => {
    const { pending } = JSON.parse((e as MessageEvent<string>).data) as { pending: number };
    enrollmentListeners.forEach((fn) => fn(pending));
  });
}

function close(): void {
  if (--refs > 0) return;
  source?.close();
  source = null;
}

interface Handlers {
  /** Frische Fleet-Zeile eines Routers (online/lastSeen/hostname/firmware/wgIp). */
  onDevice?: DeviceHandler;
  /** Neuer Zähler offener Freigaben. */
  onEnrollment?: EnrollmentHandler;
  /** Stream nur öffnen, wenn angemeldet — sonst läuft er gegen 403 in einen
   *  Reconnect-Loop. Seiten unter der Auth-Shell lassen das auf `true`. */
  enabled?: boolean;
}

/**
 * Abonniert den Fleet-Live-Stream, solange die Komponente gemountet (und
 * `enabled`) ist. Die Handler bekommen ihre Nutzlast gepusht — kein Refetch,
 * kein Intervall. Handler dürfen inline sein: sie werden über eine Ref
 * aufgerufen, der Effekt selbst hängt nur an `enabled` (kein Reconnect je Render).
 */
export function useFleetEvents({ onDevice, onEnrollment, enabled = true }: Handlers): void {
  const deviceRef = useRef(onDevice);
  const enrollmentRef = useRef(onEnrollment);

  // Refs nach jedem Render nachziehen (nicht im Render — React-Regel): die im
  // Effekt registrierten Wrapper rufen so immer den frischesten Handler.
  useEffect(() => {
    deviceRef.current = onDevice;
    enrollmentRef.current = onEnrollment;
  });

  useEffect(() => {
    if (!enabled) return;
    const onDev: DeviceHandler = (d) => deviceRef.current?.(d);
    const onEnr: EnrollmentHandler = (p) => enrollmentRef.current?.(p);
    deviceListeners.add(onDev);
    enrollmentListeners.add(onEnr);
    open();
    return () => {
      deviceListeners.delete(onDev);
      enrollmentListeners.delete(onEnr);
      close();
    };
  }, [enabled]);
}
